import { useRef, useState } from 'react';
import {
  Container,
  Card,
  CardBody,
  Table,
  Badge,
  Button,
} from 'reactstrap';
import { useFormikContext } from 'formik';
import { Typography } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { useFormikStepper } from '../../../components/hooks';
import { updateLeaseById, validateStep } from '../../utils';
import { EditLease } from './Steps';
import { validators } from '../../config';

const SignLeaseByTenants = () => {
  const leaseFormRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { values } = useFormikContext();
  const { leaseId, currentStep } = useFormikStepper();
  const { tenants = [] } = values;

  const getTenantStatus = (tenant) => {
    if (!tenant) return false;
    return !!tenant.signature?.trim();
  };

  const allSigned = tenants.length > 0 && tenants.every((tenant) => getTenantStatus(tenant));
  const signedCount = tenants.filter(tenant => getTenantStatus(tenant)).length;
  const progressPercentage = tenants.length > 0 ? (signedCount / tenants.length) * 100 : 0;

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Do you want to save these changes to the lease?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#ef4444',
        confirmButtonText: 'Yes, save changes',
        cancelButtonText: 'Cancel',
        customClass: {
          popup: 'rounded-lg',
          confirmButton: 'rounded-lg px-4 py-2',
          cancelButton: 'rounded-lg px-4 py-2'
        }
      });

      if (result.isConfirmed) {
        setIsSaving(true);
        const isStepValid = await validateStep(validators.step1, values);
        if (!isStepValid) {
          return false;
        }
        await updateLeaseById(leaseId, String(currentStep), values);
        await Swal.fire({
          title: 'Saved!',
          text: 'Your changes have been saved successfully.',
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'OK',
          customClass: {
            popup: 'rounded-lg',
            confirmButton: 'rounded-lg px-4 py-2'
          }
        });

        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error saving lease:', error);

      await Swal.fire({
        title: 'Error!',
        text: 'There was an error saving your changes. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-lg',
          confirmButton: 'rounded-lg px-4 py-2'
        }
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container
      fluid
      className="d-flex justify-content-center align-items-center"
      style={{
        minHeight: '60vh',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #3b82f6 100%)',
        padding: '3rem 1rem',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgb(255 255 255) 0%, rgb(255 255 255) 50%, rgb(186 212 255) 100%)',
          pointerEvents: 'none',
        }}
      />
      <Card
        innerRef={leaseFormRef}
        className="shadow-lg border-0"
        style={{
          maxWidth: isEditing ? '1400px' : '700px',
          width: '100%',
          borderRadius: '24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backdropFilter: 'blur(10px)',
          background: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 15px 12px rgba(0, 0, 0, 0.08)',
          position: 'relative',
          zIndex: 1,
        }}
        onMouseEnter={(e) => {
          if (!isEditing) {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 32px 64px rgba(0, 0, 0, 0.15), 0 24px 24px rgba(0, 0, 0, 0.12)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isEditing) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.1), 0 15px 12px rgba(0, 0, 0, 0.08)';
          }
        }}
      >
        <CardBody className="px-5 py-5">
          {/* Header Section */}
          <div className="text-center mb-4">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div style={{ width: '80px' }}></div>
              <div
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: allSigned
                    ? 'linear-gradient(135deg, #10b981, #059669)'
                    : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  boxShadow: allSigned
                    ? '0 8px 24px rgba(16, 185, 129, 0.4)'
                    : '0 8px 24px rgba(59, 130, 246, 0.4)',
                  animation: allSigned ? 'pulse 2s infinite' : 'none',
                }}
              >
                <FeatherIcon
                  icon={allSigned ? 'check-circle' : 'clock'}
                  size="36"
                  color="white"
                />
              </div>
              <div style={{ width: '80px', display: 'flex', justifyContent: 'flex-end' }}>
                {!isEditing && (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleEditClick}
                    className="d-flex align-items-center gap-1"
                    style={{
                      borderRadius: '12px',
                      padding: '8px 16px',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                    }}
                  >
                    <FeatherIcon icon="edit-2" size="14" />
                    Edit
                  </Button>
                )}
              </div>
            </div>

            <Typography
              variant="h5"
              fontWeight="600"
              gutterBottom
              style={{
                color: '#1e293b',
                marginBottom: '0.5rem',
                letterSpacing: '-0.01em',
                fontSize: '1.5rem'
              }}
            >
              {isEditing ? 'Edit Lease' : allSigned ? 'Lease Signatures Complete' : 'Signature Collection Status'}
            </Typography>

            <Typography
              variant="body2"
              style={{
                color: '#475569',
                fontSize: '0.95rem',
                lineHeight: '1.5',
                maxWidth: '420px',
                margin: '0 auto'
              }}
            >
              {isEditing
                ? 'Update lease information and save changes to the lease.'
                : allSigned
                  ? 'All required tenant signatures have been collected and processed.'
                  : 'Monitor signature collection progress for all assigned tenants.'}
            </Typography>
          </div>

          {!isEditing && (
            <>
              {/* Progress Section */}
              {tenants.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Typography variant="caption" fontWeight="500" style={{ color: '#475569', fontSize: '0.85rem' }}>
                      {signedCount} of {tenants.length} signatures collected
                    </Typography>
                    <Typography variant="caption" fontWeight="500" style={{ color: '#475569', fontSize: '0.85rem' }}>
                      {Math.round(progressPercentage)}%
                    </Typography>
                  </div>
                  <div
                    style={{
                      height: '8px',
                      borderRadius: '12px',
                      background: '#e0e0e0',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${progressPercentage}%`,
                        background: allSigned
                          ? 'linear-gradient(90deg, #10b981, #059669)'
                          : 'linear-gradient(90deg, #3b82f6, #2563eb)',
                        borderRadius: '12px',
                        transition: 'width 0.1s ease',
                        position: 'relative',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                          animation: progressPercentage > 0 && progressPercentage < 100 ? 'shimmer 2s infinite' : 'none',
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Tenants Table */}
              {tenants.length > 0 ? (
                <div
                  className="table-responsive"
                  style={{
                    borderRadius: '16px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden'
                  }}
                >
                  <Table className="mb-0" style={{ background: 'transparent' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)' }}>
                        <th
                          className="text-start"
                          style={{
                            borderBottom: 'none',
                            fontWeight: '500',
                            color: '#475569',
                            padding: '0.875rem 1.25rem',
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}
                        >
                          Tenant
                        </th>
                        <th
                          className="text-end"
                          style={{
                            borderBottom: 'none',
                            fontWeight: '500',
                            color: '#475569',
                            padding: '0.875rem 1.25rem',
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}
                        >
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.map((tenant, index) => {
                        const hasSigned = getTenantStatus(tenant);
                        return (
                          <tr
                            key={tenant.id || index}
                            style={{
                              borderBottom: index < tenants.length - 1 ? '1px solid #e2e8f0' : 'none',
                              transition: 'background-color 0.2s ease',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <td
                              className="text-start"
                              style={{
                                padding: '1rem 1.25rem',
                                borderBottom: 'none',
                                fontWeight: '500',
                                color: '#495057',
                                fontSize: '0.9rem'
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${hasSigned ? '#4CAF50' : '#FF9800'}, ${hasSigned ? '#45a049' : '#F57C00'})`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '12px',
                                    color: 'white',
                                    fontWeight: '600',
                                    fontSize: '12px'
                                  }}
                                >
                                  {(tenant.name || `Tenant ${index + 1}`).charAt(0).toUpperCase()}
                                </div>
                                {tenant.name || `Tenant ${index + 1}`}
                              </div>
                            </td>
                            <td
                              className="text-end"
                              style={{
                                padding: '1rem 1.25rem',
                                borderBottom: 'none'
                              }}
                            >
                              <Badge
                                color={hasSigned ? 'success' : 'warning'}
                                className="d-inline-flex align-items-center gap-1 px-2 py-1"
                                style={{
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  borderRadius: '8px',
                                  textTransform: 'none',
                                  letterSpacing: '0.01em',
                                  boxShadow: hasSigned
                                    ? '0 2px 8px rgba(76, 175, 80, 0.15)'
                                    : '0 2px 8px rgba(255, 152, 0, 0.15)',
                                  border: 'none',
                                  background: hasSigned
                                    ? 'linear-gradient(135deg, #4CAF50, #45a049)'
                                    : 'linear-gradient(135deg, #FF9800, #F57C00)',
                                  color: 'white'
                                }}
                              >
                                <FeatherIcon
                                  icon={hasSigned ? 'check' : 'clock'}
                                  size="12"
                                />
                                {hasSigned ? 'Signed' : 'Pending'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div
                  className="text-center py-5"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '16px',
                    border: '2px dashed #dee2e6'
                  }}
                >
                  <FeatherIcon icon="users" size="40" color="#adb5bd" className="mb-2" />
                  <Typography variant="body1" style={{ color: '#6c757d', marginBottom: '0.25rem', fontSize: '0.95rem', fontWeight: '500' }}>
                    No Tenants Assigned
                  </Typography>
                  <Typography variant="body2" style={{ color: '#adb5bd', fontSize: '0.8rem' }}>
                    Add tenants to this lease to begin signature collection.
                  </Typography>
                </div>
              )}

              {/* Footer Message */}
              {!allSigned && tenants.length > 0 && (
                <div className="mt-4 text-center">
                  <div
                    className="d-inline-flex align-items-center gap-2 px-4 py-2"
                    style={{
                      background: 'rgba(255, 152, 0, 0.1)',
                      borderRadius: '24px',
                      border: '1px solid rgba(255, 152, 0, 0.2)'
                    }}
                  >
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#FF9800',
                        animation: 'pulse 2s infinite'
                      }}
                    />
                    <Typography
                      variant="caption"
                      style={{
                        color: '#E65100',
                        fontWeight: '500',
                        margin: 0,
                        fontSize: '0.8rem'
                      }}
                    >
                      {tenants.length - signedCount} signature{tenants.length - signedCount !== 1 ? 's' : ''} remaining
                    </Typography>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Edit Form */}
          {isEditing && (
            <div>
              <EditLease />
              {/* Action Buttons */}
              <div className="d-flex justify-content-end gap-3 mt-4">
                <Button
                  color="secondary"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="d-flex align-items-center gap-2"
                  style={{
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    background: '#6c757d',
                    border: 'none'
                  }}
                >
                  <FeatherIcon icon="x" size="16" />
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="d-flex align-items-center gap-2"
                  style={{
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  {isSaving ? (
                    <>
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                        style={{ width: '16px', height: '16px' }}
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FeatherIcon icon="save" size="16" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export { SignLeaseByTenants };